import { Component, OnInit, Inject } from "@angular/core";
import { Addendum } from "../models/addendum";
import { AddendumService } from "../services/addendum.service";
import { ActivatedRoute, Router } from "@angular/router";
import { WebStorageService, LOCAL_STORAGE } from "ngx-webstorage-service";
import {
  UserRoleEnum,
  AddendumStatusEnum,
  Constant,
  StandardTypeEnum,
} from "../core/constant";
import { Standard } from "../models/standard";
import { StandardService } from "../services/standard.service";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { Observable } from "rxjs";
import { AddendumAttachment } from "../models/addendum-attachment";
import { SuccessDialogComponent } from "../success-dialog/success-dialog.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { CryptographyService } from "../services/cryptography.service";
import { SelectedStandard } from "../models/selected-standard";
import { FailModuleComponent } from "../fail-module/fail-module.component";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { EquipmentAddendum } from "../models/equipment-addendum";
import {
  ExceptionFieldSnapshot,
  ExceptionTimelineTracker,
} from "../models/exception-timeline-tracker";
import DOMPurify from "dompurify";
@Component({
  selector: "app-add-addendum-upcoming",
  templateUrl: "./add-addendum-upcoming.component.html",
  styleUrls: ["./add-addendum-upcoming.component.scss"],
})
export class AddAddendumUpcomingComponent implements OnInit {
  /** True when route is add-addendum-upcoming; false for add-addendum (published standards). */
  isUpcomingFlow = false;
  isEditMode = false;
  pageTitle = "Add Exception";
  loading: boolean = false;
  notes: SafeHtml;
  addendumForm: FormGroup;
  userRole: number = 2;
  addendum: Addendum = new Addendum();
  error: string = "";
  invalidFile: boolean = false;
  invalidFileMessage: string = "";
  invalidFinalFile: boolean = false;
  invalidFinalFileMessage: string = "";
  today = new Date();
  sidebarCollapsed = false;
  allLocations = "";
  subLocations = "";
  keyContactEmail = "";
  private allCountries: any[] = [];
  private editFieldSnapshot: ExceptionFieldSnapshot | null = null;
  private attachmentFileSizes = new Map<string, number>();
  get addendumStatusEnum() {
    return AddendumStatusEnum;
  }
  get userRoleEnum() {
    return UserRoleEnum;
  }
  countryId: Array<number> = null;
  countries: any = [];

  constructor(
    protected addendumService: AddendumService,
    protected standardService: StandardService,
    protected cryptographyService: CryptographyService,
    protected router: Router,
    @Inject(LOCAL_STORAGE)
    protected storage: WebStorageService,
    protected activeRoute: ActivatedRoute,
    protected fb: FormBuilder,
    protected ngbModal: NgbModal,
    protected sanitizer: DomSanitizer
  ) {
    this.isUpcomingFlow = this.router.url.includes("add-addendum-upcoming");
  }

  ngOnInit() {
    if (this.isEditMode) {
      return;
    }

    this.loading = true;
    this.addendum.standardId = parseInt(
      this.activeRoute.snapshot.paramMap.get("standardId")
    );

    this.userRole = this.cryptographyService.decryptData(
      this.storage.get("userRole")
    );
    this.addendum.createdUserId = this.cryptographyService.decryptData(
      this.storage.get("userId")
    );
    this.getMasterDataForNrbes();
    this.createForm();
    this.getNewAddendumDetails(this.addendum.standardId);
  }

  getMasterDataForNrbes(): void {
    this.standardService
      .GetMasterDataForAddNrbes(
        this.cryptographyService.decryptData(this.storage.get("userRole")),
        this.cryptographyService.decryptData(this.storage.get("userId"))
      )
      .subscribe((data: any) => {
        this.allCountries = (data.countries || []).filter((x) => x.id != 0);
        this.countries = [...this.allCountries];
        if (this.addendum.standardId && !this.isEditMode) {
          this.filterCountriesWithExistingExceptions(this.addendum.standardId);
        }
        if (this.isEditMode) {
          this.ensureEditCountriesAvailable();
        }
      });
  }

  filterCountriesWithExistingExceptions(standardId: number): void {
    this.standardService
      .getGrbesDetailInfomationByStandardId(
        standardId,
        [0],
        [],
        3,
        null,
        [1, 2, 3, 4]
      )
      .subscribe((d: any) => {
        const addendumCountries = (d.addendum || []).flatMap((x) =>
          Array.isArray(x.countryId) ? x.countryId : [x.countryId]
        );
        this.countries = this.allCountries.filter(
          (x) => !addendumCountries.includes(x.id)
        );
      });
  }
  dateFilter = (d: Date | null): boolean => {
    const date = d || new Date();
    return date > this.today;
  };
  /**
   * Create form
   * @return void
   */
  createForm(): void {
    this.addendumForm = this.fb.group({
      title: [""],
      description: ["", Validators.required],
      note: ["", Validators.required],
      status: [AddendumStatusEnum.Requested, Validators.required],
      exceptionRenewalDate: ["", Validators.required],
      requestedOn: [null, Validators.required],
      statusOn: [null],
      country: [[], Validators.required],
    });
    this.addendumForm
      .get("status")
      .valueChanges.subscribe((status) => this.applyStatusDateRules(status));
    this.applyStatusDateRules(AddendumStatusEnum.Requested);
  }

  showStatusOnField(): boolean {
    const status = Number(this.addendumForm?.get("status")?.value);
    return (
      status === AddendumStatusEnum.Approved ||
      status === AddendumStatusEnum.Rejected ||
      status === AddendumStatusEnum.Expired
    );
  }

  isNewStatus(): boolean {
    return (
      Number(this.addendumForm?.get("status")?.value) ===
      AddendumStatusEnum.Requested
    );
  }

  get statusOnLabel(): string {
    const status = Number(this.addendumForm?.get("status")?.value);
    switch (status) {
      case AddendumStatusEnum.Approved:
        return "Approved On";
      case AddendumStatusEnum.Rejected:
        return "Rejected On";
      case AddendumStatusEnum.Expired:
        return "Expired On";
      default:
        return "";
    }
  }

  private applyStatusDateRules(status: AddendumStatusEnum): void {
    const normalizedStatus = Number(status);
    const statusOn = this.addendumForm.get("statusOn");
    if (!statusOn) {
      return;
    }
    statusOn.setValue(null, { emitEvent: false });
    if (
      normalizedStatus === AddendumStatusEnum.Approved ||
      normalizedStatus === AddendumStatusEnum.Rejected ||
      normalizedStatus === AddendumStatusEnum.Expired
    ) {
      statusOn.setValidators(Validators.required);
    } else {
      statusOn.clearValidators();
    }
    statusOn.updateValueAndValidity({ emitEvent: false });

    if (normalizedStatus === AddendumStatusEnum.Requested) {
      this.onRemoveFinalDocument();
    }
  }

  /**
   * Get a new Addendum details
   * @return void
   */
  getNewAddendumDetails(standardId: number): void {
    this.loading = true;
    this.standardService
      .getStandardInfomationByStandardId(standardId)
      .subscribe((data: SelectedStandard) => {
        this.addendum = new Addendum();
        this.addendum.standard = new Standard(data.standard);
        const safeHTML = DOMPurify.sanitize(this.addendum.standard.note);
        this.notes = this.sanitizer.bypassSecurityTrustHtml(safeHTML);
        this.addendum.standardId = this.addendum.standard.id;
        this.addendumForm.controls.title.setValue(
          `Exception for ${this.addendum.standard.title}`
        );
        this.setStandardSidebarFields(data.standard);
        this.filterCountriesWithExistingExceptions(this.addendum.standardId);
        this.loading = false;
        (error: string) => {
          this.loading = false;
          this.error = error;
        };
      });
  }

  submitTheAddendum() {
    if (this.addendumForm.invalid) {
      this.addendumForm.markAllAsTouched();
      return;
    }
    const formValue = this.addendumForm.value;
    const status = Number(formValue.status);
    const addendumCopy: any = {
      id: this.isEditMode ? this.addendum.id : undefined,
      standardId: this.addendum.standardId,
      standard: this.addendum.standard,
      title: formValue.title,
      description: formValue.description,
      note: formValue.note,
      addendumStatusId: status,
      created: this.toUtcDate(formValue.requestedOn),
      exceptionRenewalDate: this.toUtcDate(formValue.exceptionRenewalDate),
      expiryDate: this.toUtcDate(formValue.exceptionRenewalDate),
      approvedOrRejectedDate:
        status === AddendumStatusEnum.Approved ||
          status === AddendumStatusEnum.Rejected ||
          status === AddendumStatusEnum.Expired
          ? this.toUtcDate(formValue.statusOn)
          : null,
      countryIds: formValue.country,
      createdUserId: this.cryptographyService.decryptData(
        this.storage.get("userId")
      ),
      addendumAttachment: this.buildAttachmentsForSubmit(),
    };
    this.saveAddendum(addendumCopy);
  }

  private buildAttachmentsForSubmit(): any[] {
    return (this.addendum.addendumAttachment || []).map((attachment) => ({
      id: attachment.id || 0,
      addendumId: this.addendum.id,
      fileName: attachment.fileName,
      isRequestedFile: attachment.isRequestedFile,
      attachmentContentAsBase64: attachment.attachmentContentAsBase64 || null,
    }));
  }

  private toUtcDate(value: Date | string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
      return null;
    }
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    ).toISOString();
  }

  onSelectAAddendumDocument(event) {
    this.invalidFileMessage = "";
    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      return;
    }
    this.invalidFile =
      this.validateFileType(selectedFile.name) ||
      this.validateFileSize(selectedFile);
    if (this.invalidFile) {
      return;
    }
    this.convertToBaseString(selectedFile).subscribe((result) => {
      this.setRequestedAttachment(selectedFile.name, result);
      this.attachmentFileSizes.set(selectedFile.name, selectedFile.size);
      event.target.value = null;
    });
  }

  onSelectFinalDocument(event) {
    this.invalidFinalFileMessage = "";
    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      return;
    }
    this.invalidFinalFile =
      this.validateFinalFileType(selectedFile.name) ||
      this.validateFinalFileSize(selectedFile);
    if (this.invalidFinalFile) {
      return;
    }
    this.convertToBaseString(selectedFile).subscribe((result) => {
      this.setFinalAttachment(selectedFile.name, result);
      this.attachmentFileSizes.set(`final:${selectedFile.name}`, selectedFile.size);
      event.target.value = null;
    });
  }

  private setRequestedAttachment(fileName: string, content: string): void {
    const others = (this.addendum.addendumAttachment || []).filter(
      (a) => a.isRequestedFile === false
    );
    const attachment = new AddendumAttachment();
    attachment.id = 0;
    attachment.fileName = fileName;
    attachment.addendumId = this.addendum.id;
    attachment.isRequestedFile = true;
    attachment.attachmentContentAsBase64 = content;
    this.addendum.addendumAttachment = [attachment, ...others];
  }

  private setFinalAttachment(fileName: string, content: string): void {
    const requested = (this.addendum.addendumAttachment || []).filter(
      (a) => a.isRequestedFile !== false
    );
    const attachment = new AddendumAttachment();
    attachment.id = 0;
    attachment.fileName = fileName;
    attachment.addendumId = this.addendum.id;
    attachment.isRequestedFile = false;
    attachment.attachmentContentAsBase64 = content;
    this.addendum.addendumAttachment = [...requested, attachment];
  }

  getRequestedAttachment(): AddendumAttachment | undefined {
    return this.addendum.addendumAttachment?.find(
      (a) => a.isRequestedFile !== false
    );
  }

  getFinalAttachment(): AddendumAttachment | undefined {
    return this.addendum.addendumAttachment?.find(
      (a) => a.isRequestedFile === false
    );
  }

  getAttachmentSize(attachment: AddendumAttachment): string {
    if (!attachment?.fileName) {
      return "";
    }
    const key =
      attachment.isRequestedFile === false
        ? `final:${attachment.fileName}`
        : attachment.fileName;
    const bytes = this.attachmentFileSizes.get(key);
    if (!bytes) {
      return "";
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    return `${Math.round(bytes / 1024)} KB`;
  }

  validateFinalFileType(fileName: string) {
    const invalid = this.validateFileType(fileName);
    if (invalid) {
      this.invalidFinalFileMessage = this.invalidFileMessage;
    }
    return invalid;
  }

  validateFinalFileSize(file: any) {
    const invalid = this.validateFileSize(file);
    if (invalid) {
      this.invalidFinalFileMessage = this.invalidFileMessage;
    }
    return invalid;
  }

  convertToBaseString(selectedFile): Observable<any> {
    return new Observable<any>((observer) => {
      var fileReader = new FileReader();
      if (fileReader && selectedFile) {
        fileReader.readAsDataURL(selectedFile);
        fileReader.onload = function () {
          observer.next(fileReader.result.toString());
        };
      }
    });
  }

  onRemoveAddendumDocument() {
    this.addendum.addendumAttachment = (
      this.addendum.addendumAttachment || []
    ).filter((a) => a.isRequestedFile === false);
    this.invalidFile = false;
  }

  onRemoveFinalDocument() {
    this.addendum.addendumAttachment = (
      this.addendum.addendumAttachment || []
    ).filter((a) => a.isRequestedFile !== false);
    this.invalidFinalFile = false;
  }

  goBack(): void {
    this.router.navigate([this.getBackRoute()]);
  }

  protected getBackRoute(): string {
    if (this.isEditMode && this.addendum?.id) {
      return `/addendum/${this.addendum.id}`;
    }
    return this.getExceptionListRoute();
  }

  protected loadExistingAddendum(addendumId: number): void {
    this.loading = true;
    this.addendumService
      .getAddendumInformationByAddendumId(addendumId)
      .subscribe((data: any) => {
        if (!data) {
          this.loading = false;
          const modalRef = this.ngbModal.open(FailModuleComponent);
          modalRef.componentInstance.errorMessage =
            "This exception is not available for editing.";
          return;
        }

        data.country = Array.isArray(data.country)
          ? data.country
          : [data.country];
        data.countryId = Array.isArray(data.countryId)
          ? data.countryId
          : [data.countryId];
        this.addendum = new Addendum(data, this.userRole);
        this.populateFormFromAddendum();
        if (this.isEditMode) {
          this.editFieldSnapshot = ExceptionTimelineTracker.buildSnapshot(
            this.addendum
          );
        }

        this.standardService
          .getStandardInfomationByStandardId(this.addendum.standardId)
          .subscribe((standardData: SelectedStandard) => {
            this.addendum.standard = new Standard(standardData.standard);
            const safeHTML = DOMPurify.sanitize(this.addendum.standard.note);
            this.notes = this.sanitizer.bypassSecurityTrustHtml(safeHTML);
            this.setStandardSidebarFields(standardData.standard);
            this.ensureEditCountriesAvailable();
            this.loading = false;
          });
      });
  }

  protected populateFormFromAddendum(): void {
    const countryIds = (this.addendum.countryId || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    const statusOnDate = this.addendum.approvedOrRejectedDate;

    this.addendumForm.patchValue({
      title:
        this.addendum.title ||
        (this.addendum.standard?.title
          ? `Exception for ${this.addendum.standard.title}`
          : ""),
      description: this.addendum.description || "",
      note: this.addendum.note || "",
      status: this.addendum.addendumStatusId,
      exceptionRenewalDate:
        this.addendum.exceptionRenewalDate || this.addendum.expiryDate,
      requestedOn: this.addendum.created,
      country: countryIds,
    });
    this.applyStatusDateRules(this.addendum.addendumStatusId);
    if (statusOnDate) {
      this.addendumForm
        .get("statusOn")
        ?.setValue(statusOnDate, { emitEvent: false });
    }
  }

  protected ensureEditCountriesAvailable(): void {
    const selectedIds: number[] =
      this.addendumForm?.get("country")?.value || [];
    const selectedCountries = (this.addendum.country || []).filter((country) =>
      selectedIds.includes(country.id)
    );
    const merged = [...this.allCountries];
    selectedCountries.forEach((country) => {
      if (!merged.some((item) => item.id === country.id)) {
        merged.push(country);
      }
    });
    this.countries = merged.length ? merged : [...this.allCountries];
  }

  private getExceptionListRoute(): string {
    if (this.addendumService.isCommingFromAllStandards) {
      return "/view-all-standards";
    }
    if (this.addendumService.isCommingFromNationalEquipmentList) {
      return "/view-all-equipments";
    }
    if (this.addendumService.isCommingFromNrbes) {
      if (
        this.addendum.standard?.standardTypeId ==
        StandardTypeEnum.GlobalEquipmentStandard
      ) {
        return "/view-all-equipments";
      }
      return "/nrbeslist";
    }
    if (this.isUpcomingFlow) {
      return "/view-all-upcoming-standards-list";
    }
    if (
      this.addendum.standard?.standardTypeId ==
      StandardTypeEnum.GlobalEquipmentStandard
    ) {
      return "/equipment-addendum-list";
    }
    return "/addendum-list";
  }

  private navigateAfterExceptionSaved(): void {
    this.router.navigate([this.getExceptionListRoute()]);
  }

  viewCompleteStandard(): void {
    if (!this.addendum.standardId) {
      return;
    }
    if (
      this.addendum.standard?.standardTypeId ==
      StandardTypeEnum.GlobalEquipmentStandard
    ) {
      if (
        this.addendumService.isCommingFromNationalEquipmentList &&
        this.addendumService.isCommingFromNrbes
      ) {
        this.router.navigate([
          "/view-national-equipments-standard-detail",
          this.addendum.standardId,
        ]);
      } else {
        this.router.navigate([
          "/view-equipments-standard-detail",
          this.addendum.standardId,
        ]);
      }
      return;
    }
    if (this.isUpcomingFlow) {
      this.router.navigate([
        "/view-upcoming-standard-detail",
        this.addendum.standardId,
      ]);
      return;
    }
    this.router.navigate([
      "/view-standard-detail",
      this.addendum.standardId,
    ]);
  }

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  private setStandardSidebarFields(standard: any): void {
    const locations = standard?.standardLocation;
    if (locations?.length) {
      this.allLocations = locations
        .map((loc) => loc.locationType?.name)
        .filter(Boolean)
        .join(", ");
      this.subLocations = locations
        .map((loc) =>
          loc.locationType?.subLocationTypes?.length
            ? loc.locationType.subLocationTypes.map((sloc) => sloc.name).join(", ")
            : ""
        )
        .filter(Boolean)
        .join(", ");
    } else {
      this.allLocations = "";
      this.subLocations = "";
    }
    this.keyContactEmail = standard?.keyContact?.email || "";
  }

  private isEquipmentException(): boolean {
    return (
      this.addendum.standard?.standardTypeId ===
      StandardTypeEnum.GlobalEquipmentStandard
    );
  }

  private createAddendumObservable(addendumCopy: any) {
    if (this.isEquipmentException()) {
      const equipmentAddendum = this.toEquipmentAddendumPayload(addendumCopy);
      return this.addendumService.addEquipmentAddendum(equipmentAddendum);
    }
    return this.addendumService.addAddendum(addendumCopy);
  }

  private toEquipmentAddendumPayload(addendumCopy: any): EquipmentAddendum {
    const payload = new EquipmentAddendum();
    Object.assign(payload, addendumCopy);
    payload.countryId = addendumCopy.countryIds ?? addendumCopy.countryId;
    if (this.addendum.standard?.equipmentStandard) {
      payload.equipmentStandard = this.addendum.standard.equipmentStandard;
    }
    return payload;
  }

  private saveAddendum(addendum: any) {
    this.loading = true;
    if (addendum.id) {
      addendum.modifiedUserId = this.cryptographyService.decryptData(
        this.storage.get("userId")
      );
      this.addendumService.updateAddendum(addendum).subscribe(() => {
        if (this.isEditMode && this.editFieldSnapshot && addendum.id) {
          ExceptionTimelineTracker.recordEdit(
            addendum.id,
            this.editFieldSnapshot,
            addendum
          );
        }
        this.loading = false;
        const modalRef = this.ngbModal.open(SuccessDialogComponent);
        modalRef.componentInstance.title = this.isEditMode
          ? "Exception Updated"
          : "Exception Updated";
        modalRef.componentInstance.message = this.isEditMode
          ? "The exception has been updated successfully."
          : "The exception has been updated successfully.";
        modalRef.componentInstance.showViewButton = this.isEditMode;

        modalRef.componentInstance.nrbesSuccessPopupEmit.subscribe(
          (action: string) => {
            this.ngbModal.dismissAll();
            if (action === "view") {
              this.router.navigate([this.getAddendumListRoute()]);
              return;
            }
            if (this.isEditMode) {
              this.router.navigate(["/addendum/" + addendum.id]);
              return;
            }
            this.navigateAfterExceptionSaved();
          }
        );
        (error: string) => {
          this.loading = false;
          this.error = error;
        };
      });
    } else {
      this.createAddendumObservable(addendum).subscribe((result) => {
        if (result == false) {
          this.loading = false;
          const modalRef = this.ngbModal.open(SuccessDialogComponent);
          modalRef.componentInstance.title = "Error";
          modalRef.componentInstance.message =
            "Exception already added for the standard.";

          modalRef.componentInstance.nrbesSuccessPopupEmit.subscribe(() => {
            this.ngbModal.dismissAll();
            if (
              addendum.standard.standardTypeId ==
              StandardTypeEnum.GlobalEquipmentStandard
            ) {
              this.navigateAfterExceptionSaved();
            } else {
              this.navigateAfterExceptionSaved();
            }
          });
        } else {
          this.loading = false;
          const modalRef = this.ngbModal.open(SuccessDialogComponent);
          modalRef.componentInstance.title = "Exception Added";
          modalRef.componentInstance.message =
            "The exception has been added successfully and is now effective for the configured market and applicability.";
          modalRef.componentInstance.showViewButton = true;

          modalRef.componentInstance.nrbesSuccessPopupEmit.subscribe(
            (action: string) => {
              this.ngbModal.dismissAll();
              if (action === "view") {
                this.router.navigate([this.getAddendumListRoute()]);
                return;
              }
              this.navigateAfterExceptionSaved();
            }
          );
        }
      });
    }
  }

  private getAddendumListRoute(): string {
    return this.isEquipmentException()
      ? "/equipment-addendum-list"
      : "/addendum-list";
  }

  downloadAttachment(attachment: AddendumAttachment | undefined) {
    if (!attachment) {
      return;
    }
    if (attachment.id) {
      return this.onDownloadSelectedAddendumDocument(attachment.id);
    }
    if (!attachment.attachmentContentAsBase64) {
      return;
    }
    fetch(attachment.attachmentContentAsBase64)
      .then((res) => res.blob())
      .then((blob) => {
        if ((window.navigator as any).msSaveOrOpenBlob) {
          (window.navigator as any).msSaveBlob(blob, attachment.fileName);
        } else {
          var a = document.createElement("a");
          a.href = attachment.attachmentContentAsBase64;
          a.setAttribute("download", attachment.fileName);
          a.click();
        }
      });
  }

  onDownloadSelectedAddendumDocument(id) {
    if (id) {
      return this.standardService.getAttachmentUrl(id, true).subscribe(
        (response: any) => {
          const blob = new Blob([response], {
            type: response.type,
          });
          if (
            (window.navigator as any) &&
            (window.navigator as any).msSaveOrOpenBlob
          ) {
            (window.navigator as any).msSaveOrOpenBlob(blob);
            return;
          }
          const url = window.URL.createObjectURL(blob);
          window.open(url);
        },
        (error: any) => {
          const modalRef = this.ngbModal.open(FailModuleComponent);
          modalRef.componentInstance.errorMessage = error.error
            ? error.error.Message
            : error.message;
        }
      );
    }
  }

  validateFileType(fileName: string) {
    let fileTypeArr = ["docx", "doc"];
    if (fileName != null) {
      let fileNameArr = [];
      let splitedFileName = fileName.toLowerCase().split(".");
      splitedFileName.forEach(function (obj) {
        fileNameArr.push(obj);
      });
      fileNameArr = fileNameArr.reverse();
      if (!fileTypeArr.includes(fileNameArr[0])) {
        this.invalidFileMessage = "Invalid file type.";
        return true;
      }
    }
  }

  validateFileSize(file: any) {
    if (file.size > 10485760) {
      this.invalidFileMessage = "File size exceeds maximum limit 10 MB.";
      return true;
    }
  }
}
